import { Directive, Input, computed, inject, signal } from '@angular/core';
import { Process } from '@partners/api';
import { AccountStatusEnum } from '@partners/constants';
import { IS_CARD_PROCESS, IS_LOAN_PROCESS, PolicyService } from '../../../process';
import { TemplateAssertDirective } from '../template-assert.directive';

type TMultiAssertScope = 'all' | 'atLeastOne' | 'none';

/**
 * @description
 * this is a structural directive, it will render its guarded context only if the
 * authentication state allows it, meaning: if user IS authenticated
 */

@Directive({
  standalone: true,
  selector: '[multiAssertion]'
})
export class MultiAssertionDirective extends TemplateAssertDirective {
  private _process$$ = signal<keyof typeof Process>(undefined);
  private _account$$ = signal<keyof typeof AccountStatusEnum>(undefined);
  private _scope$$ = signal<TMultiAssertScope>('all');

  @Input()
  multiAssertion: null;

  @Input()
  set multiAssertionProcess(value: keyof typeof Process) {
    this._process$$.set(value);
  }

  @Input()
  set multiAssertionAccount(value: keyof typeof AccountStatusEnum) {
    this._account$$.set(value);
  }

  @Input()
  set multiAssertionScope(value: TMultiAssertScope) {
    this._scope$$.set(value);
  }

  readonly isCardProcess$$ = inject(IS_CARD_PROCESS);
  readonly isLoanProcess$$ = inject(IS_LOAN_PROCESS);
  readonly isNewAccount$$ = computed(() => this._policyService.isNewAccount$$());
  readonly isExistentAccount$$ = computed(() => !this.isNewAccount$$());

  readonly _policyService = inject(PolicyService);

  override readonly isMatch$$ = computed<boolean>(() => {
    let isMatchProcess = undefined;
    let isMatchAccount = undefined;

    switch (this._process$$()) {
      case 'card': {
        isMatchProcess = this.isCardProcess$$();
        break;
      }
      case 'loan': {
        isMatchProcess = this.isLoanProcess$$();
        break;
      }
      default: {
        isMatchProcess = false;
      }
    }

    switch (this._account$$()) {
      case 'new': {
        isMatchAccount = this.isNewAccount$$();
        break;
      }
      case 'existent': {
        isMatchAccount = this.isExistentAccount$$();
        break;
      }
      default: {
        isMatchAccount = false;
      }
    }

    if (this._scope$$() === 'all') {
      return isMatchAccount && isMatchProcess;
    }

    if (this._scope$$() === 'atLeastOne') {
      return isMatchAccount || isMatchProcess;
    }

    return !isMatchAccount && !isMatchProcess;
  });

  constructor() {
    super();
  }
}
